import React, { useEffect, useRef } from 'react';

declare global {
    interface Window {
        hcaptcha?: any;
    }
}

const SCRIPT_URL = 'https://js.hcaptcha.com/1/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

/**
 * Loads the hCaptcha api script once.
 */
const loadScript = (): Promise<void> => {
    if (window.hcaptcha) {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error('hCaptcha script could not be loaded'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

interface HCaptchaProps {
    siteKey: string;
    /** Called with the response token when solved, and with '' when it expired or failed. */
    onToken: (token: string) => void;
    /** Changing this value resets the widget (a token can only be verified once). */
    resetKey: number;
}

const HCaptcha = ({ siteKey, onToken, resetKey }: HCaptchaProps) => {
    const container = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string | null>(null);
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        let cancelled = false;
        loadScript()
                .then(() => {
                    if (cancelled || !container.current || widgetId.current !== null) {
                        return;
                    }
                    widgetId.current = window.hcaptcha.render(container.current, {
                        sitekey: siteKey,
                        callback: (token: string) => onTokenRef.current(token),
                        'expired-callback': () => onTokenRef.current(''),
                        'error-callback': () => onTokenRef.current('')
                    });
                })
                .catch((error) => console.warn(error));
        return () => {
            cancelled = true;
            if (widgetId.current !== null && window.hcaptcha) {
                window.hcaptcha.remove(widgetId.current);
                widgetId.current = null;
            }
        };
    }, [siteKey]);

    useEffect(() => {
        if (widgetId.current !== null && window.hcaptcha) {
            window.hcaptcha.reset(widgetId.current);
            onTokenRef.current('');
        }
    }, [resetKey]);

    return <div ref={container} style={{ margin: '1em 0' }}/>;
};

export default HCaptcha;
